import { SERVICE_ACCOUNT_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { getAccountType } from '@silverfort-nodelibs/common/utils/service-account-utils';
import {
    Alert,
    Button,
    ButtonSizes,
    ButtonStyles,
    Drawer,
    EntityChip,
    List,
    ListItem,
    MenuButton,
    showAlert,
    Tabs,
    Tooltip
} from '@silverfort/sf-components';
import { FC, useCallback, useMemo, useState } from 'react';
import ProtectionBadge from '../../components/ProtectionBadge';
import RiskChip from '../../components/RiskChip';
import { httpPut } from '../../../../../components/utils';
import ActivityTab from './ActivityTab/ActivityTab';
import PropertiesTab from './PropertiesTab';
import ProtectionSettingsTab from './ProtectionSettingsTab';
import { useServiceAccountContext } from '../../contexts/ServiceAccountContext';
import { Pages, Roles } from '../../../../../context/contexts/UserContext/permissionsTypes';
import useHasPermissions from '../../../../../hooks/useHasPermissions';
import { TabProps } from '@silverfort/sf-components/dist/Tabs/Tab';
import { TOOLTIP_PROP_OPTIONS } from '../../../../../components/common/LockedFeatures';
import { ServiceAccountRemoveReason } from './ServiceAccountRemoveReason';
import { convertServiceAccountDataToServiceAccount } from '../../utils/ServiceAccountsUtils';
import ActionPopups from '../../components/ActionPopups';

export const SERVICE_ACCOUNT_TYPES_TO_ACCOUNT_MAPPING = {
    [SERVICE_ACCOUNT_TYPES.MACHINE_TO_MACHINE.type]: 'Service Account',
    [SERVICE_ACCOUNT_TYPES.HYBRID.type]: 'Hybrid Account',
    [SERVICE_ACCOUNT_TYPES.SCANNER.type]: 'Service Account',
    [SERVICE_ACCOUNT_TYPES.DORMANT.type]: 'Service Account'
};

const LICENSE_TOOLTIP_PROPS = {
    title: TOOLTIP_PROP_OPTIONS.NHI_PROTECTION_LICENSE_REQUIRED.title,
    buttonTitle: 'Get Access',
    onButtonClick: TOOLTIP_PROP_OPTIONS.NHI_PROTECTION_LICENSE_REQUIRED.onGetAccessClick,
    description: TOOLTIP_PROP_OPTIONS.NHI_PROTECTION_LICENSE_REQUIRED.description
};

const DISCARDED_PROTECTION_TOOLTIP_PROPS = {
    title: 'Protection is not available for Discarded Accounts'
};

enum TAB_IDS {
    PROPERTIES = 0,
    ACTIVITY = 1,
    PROTECTION_SETTINGS = 2
}

type PopupType = 'change' | 'discard' | 'hide';
type ActionError = 'discarding' | 'updating' | 'reinstating';

const showAlertError = (action: ActionError) => {
    showAlert(
        `An error occurred ${action} the account. Please try again. If the problem persists, contact your Silverfort Representative.`,
        'error'
    );
};

const ServiceAccountsDrawer: FC = () => {
    const [selectedTab, setSelectedTab] = useState(TAB_IDS.PROPERTIES);
    const [openPopup, setOpenPopup] = useState<PopupType | undefined>();

    const {
        selectedServiceAccount,
        updatedServiceAccount,
        clearSelectedServiceAccount,
        serviceAccountFieldsToUpdate,
        isFetchingServiceAccountData,
        selectServiceAccount,
        onServiceAccountUpdate,
        onPremNHILicenseProtectionEnabled,
        onPremNHILicenseISPMEnabled,
        onPremNHILicenseITDREnabled,
        onRemove
    } = useServiceAccountContext();

    const isEditor = useHasPermissions({ permission: Pages.SERVICE_ACCOUNTS, role: Roles.EDITOR });

    const isOpen = useMemo(
        () => isFetchingServiceAccountData || !!selectedServiceAccount,
        [selectedServiceAccount, isFetchingServiceAccountData]
    );

    const accountType = useMemo(
        () =>
            updatedServiceAccount
                ? getAccountType(
                      updatedServiceAccount.is_gmsa,
                      updatedServiceAccount.type_resolved,
                      updatedServiceAccount.remove_reason
                  )
                : '',
        [updatedServiceAccount]
    );

    const onCloseDrawer = useCallback(() => {
        clearSelectedServiceAccount();
        setSelectedTab(TAB_IDS.PROPERTIES);
    }, [clearSelectedServiceAccount]);

    const onClosePopup = useCallback(() => setOpenPopup(undefined), []);

    const onReinstate = useCallback(
        async (category) => {
            if (!selectedServiceAccount) {
                showAlertError('reinstating');
                return;
            }
            try {
                const response: { data: { success: true; error: string } | null } = await httpPut(
                    '/ServiceAccountsV2/reinstate',
                    {
                        serviceAccountGuid: selectedServiceAccount.guid,
                        serviceAccountIdentifier: selectedServiceAccount.display_name,
                        oldCategory: selectedServiceAccount.type_resolved,
                        category
                    }
                );
                if (response.data?.error) {
                    showAlertError('reinstating');
                    return;
                }
                selectServiceAccount();
                onServiceAccountUpdate();
            } catch (err) {
                showAlertError('reinstating');
            }
        },
        [selectServiceAccount, selectedServiceAccount, onServiceAccountUpdate]
    );

    const onSave = useCallback(async () => {
        try {
            if (!selectedServiceAccount) {
                showAlertError('updating');
                return;
            }
            const response: { data: { success: true; error: string } | null } = await httpPut(
                '/ServiceAccountsV2/update',
                {
                    serviceAccountData: selectedServiceAccount,
                    updatedServiceAccount: serviceAccountFieldsToUpdate
                }
            );
            if (response.data?.error) {
                showAlertError('updating');
                return;
            }
            onServiceAccountUpdate();
            onCloseDrawer();
        } catch (err) {
            showAlertError('updating');
        }
    }, [selectedServiceAccount, serviceAccountFieldsToUpdate, onServiceAccountUpdate, onCloseDrawer]);

    const getProtectionTooltipProps = useCallback(() => {
        if (!onPremNHILicenseProtectionEnabled) {
            return LICENSE_TOOLTIP_PROPS;
        }

        if (updatedServiceAccount?.remove_reason || !onPremNHILicenseProtectionEnabled) {
            return DISCARDED_PROTECTION_TOOLTIP_PROPS;
        }
        return undefined;
    }, [updatedServiceAccount, onPremNHILicenseProtectionEnabled]);

    const tabs: TabProps[] = useMemo(
        () => [
            { name: 'Properties', value: TAB_IDS.PROPERTIES },
            { name: 'Activity', value: TAB_IDS.ACTIVITY },
            {
                name: 'Protection Settings',
                value: TAB_IDS.PROTECTION_SETTINGS,
                tooltipProps: getProtectionTooltipProps()
            }
        ],
        [getProtectionTooltipProps]
    );

    const disabledTabs = useMemo(
        () =>
            updatedServiceAccount
                ? updatedServiceAccount.remove_reason || !onPremNHILicenseProtectionEnabled
                    ? [TAB_IDS.PROTECTION_SETTINGS]
                    : []
                : tabs.map(({ value }) => value),
        [updatedServiceAccount, tabs, onPremNHILicenseProtectionEnabled]
    );

    return (
        <>
            <Drawer
                className="w-[840px]"
                contentClassName="p-[0px] !p-[0px]"
                open={isOpen}
                footerActionTitle="Save"
                footerActionProps={{ disabled: !isEditor || !Object.keys(serviceAccountFieldsToUpdate).length }}
                footerCloseTitle="Cancel"
                headerTitle={'Manage Account'}
                onAction={onSave}
                onClose={onCloseDrawer}>
                <div className="flex flex-col w-[840px] h-full overflow-hidden gap-6 font-sf">
                    <div className="flex justify-between border-b px-8 py-4">
                        <div className="flex gap-4">
                            <div className="flex flex-col gap-2">
                                {updatedServiceAccount ? (
                                    <>
                                        <span className="text-14 font-medium text-sf-gray-800 flex gap-1">
                                            <Tooltip
                                                title={
                                                    updatedServiceAccount.identifier ||
                                                    updatedServiceAccount.display_name
                                                }>
                                                <div className="truncate overflow-hidden w-[240px] ">
                                                    {updatedServiceAccount.identifier ||
                                                        updatedServiceAccount.display_name}
                                                </div>
                                            </Tooltip>
                                            <div className="flex gap-2">
                                                {onPremNHILicenseProtectionEnabled && (
                                                    <ProtectionBadge
                                                        isProtected={updatedServiceAccount.is_protected}
                                                        protectedMode={updatedServiceAccount.protected_mode}
                                                    />
                                                )}
                                                {(onPremNHILicenseISPMEnabled || onPremNHILicenseITDREnabled) && (
                                                    <RiskChip risk={updatedServiceAccount.risk} />
                                                )}
                                                {updatedServiceAccount.is_internal_sa && (
                                                    <EntityChip
                                                        className="min-w-[170px]"
                                                        text={'Silverfort service account'}
                                                    />
                                                )}
                                            </div>
                                        </span>
                                        <span className="text-12 font-normal text-sf-gray-500">{accountType}</span>
                                    </>
                                ) : (
                                    <div className="animate-pulse flex gap-2">
                                        <div className="h-4 w-50 rounded bg-sf-gray-50"></div>
                                        <div className="h-4 w-20 rounded bg-sf-gray-50"></div>
                                        <div className="h-4 w-20 rounded bg-sf-gray-50"></div>
                                    </div>
                                )}
                            </div>
                        </div>
                        {updatedServiceAccount &&
                            (updatedServiceAccount.remove_reason ? (
                                <Button
                                    disabled={!isEditor}
                                    text="Reinstate"
                                    onClick={() => setOpenPopup('change')}
                                    buttonSize={ButtonSizes.Small}
                                    buttonStyle={ButtonStyles.Primary}
                                />
                            ) : (
                                <MenuButton
                                    disabled={!isEditor}
                                    className="right-0"
                                    openByDefault={false}
                                    content={
                                        <List className="p-[0px] min-w-[208px]">
                                            <ListItem
                                                title={`Not a ${
                                                    SERVICE_ACCOUNT_TYPES_TO_ACCOUNT_MAPPING[
                                                        updatedServiceAccount.type_resolved
                                                    ]
                                                }`}
                                                value={'discard'}
                                                onClick={setOpenPopup}
                                            />
                                            <ListItem
                                                title={`Hide ${
                                                    SERVICE_ACCOUNT_TYPES_TO_ACCOUNT_MAPPING[
                                                        updatedServiceAccount.type_resolved
                                                    ]
                                                }`}
                                                value={'hide'}
                                                onClick={setOpenPopup}
                                            />
                                        </List>
                                    }
                                />
                            ))}
                    </div>
                    {updatedServiceAccount && updatedServiceAccount.remove_reason && (
                        <ServiceAccountRemoveReason serviceAccount={updatedServiceAccount} />
                    )}
                    <div className="h-full flex flex-col gap-8 px-6 pb-8 overflow-hidden">
                        <Tabs
                            className="w-fit-content"
                            tabs={tabs}
                            onClick={setSelectedTab}
                            selectedTab={selectedTab}
                            disabledTabs={disabledTabs}
                        />
                        {tabs[selectedTab].name === 'Properties' && <PropertiesTab />}
                        {tabs[selectedTab].name === 'Activity' && <ActivityTab />}
                        {tabs[selectedTab].name === 'Protection Settings' && <ProtectionSettingsTab />}
                    </div>
                </div>
            </Drawer>
            {!!openPopup && updatedServiceAccount && (
                <ActionPopups
                    openPopup={openPopup}
                    onClosePopup={onClosePopup}
                    onRemove={onRemove}
                    serviceAccounts={[convertServiceAccountDataToServiceAccount(updatedServiceAccount)]}
                    onReinstate={onReinstate}
                />
            )}
            <Alert />
        </>
    );
};

export default ServiceAccountsDrawer;
